import type { ConsolidatedCenterLayerData } from '@/domains/centerLayer/centerLayer.entity';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  CenterLayerStoreEntity,
  CenterLayerStoreRepository,
} from '../models';

/**
 * Valores por defecto del store
 */
const defaultCenterLayerState: CenterLayerStoreEntity = {
  apiData: null,
  specificFilters: {
    jornada: null,
  },
  careerFilter: {
    selectedArea: null,
    selectedCareers: [],
  },
  isLoading: false,
};

/**
 * Store de Zustand para vista de Capa de Centro
 * Gestiona datos agrupados por sede/centro y filtros de carrera/jornada
 * Persiste: specificFilters y careerFilter en localStorage
 */
export const useCenterLayerStore = create<CenterLayerStoreRepository>()(
  persist(
    (set, get) => ({
      ...defaultCenterLayerState,

      /**
       * Carga datos de la capa de centro desde la API
       * Previene actualizaciones innecesarias comparando con datos existentes
       *
       * @param apiResponse - Respuesta de la API con datos agrupados por centro
       */
      loadData: (apiResponse: ConsolidatedCenterLayerData) => {
        const { apiData } = get();
        if (JSON.stringify(apiData) === JSON.stringify(apiResponse)) return;

        set({
          apiData: apiResponse,
          isLoading: false,
        });
      },

      /**
       * Actualiza filtros específicos de la vista (jornada, etc.)
       * Combina nuevos filtros con los existentes
       *
       * @param filters - Objeto con filtros específicos
       */
      setSpecificFilters: (filters) => {
        set((state) => ({
          specificFilters: { ...state.specificFilters, ...filters },
        }));
      },

      /**
       * Actualiza filtros de carrera (área y carreras seleccionadas)
       *
       * @param filter - Objeto con selectedArea y/o selectedCareers
       */
      setCareerFilter: (filter) => {
        set((state) => ({
          careerFilter: { ...state.careerFilter, ...filter },
        }));
      },

      /**
       * Controla el estado de carga de datos
       *
       * @param loading - true cuando se están cargando datos
       */
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      /**
       * Resetea el store a su estado inicial
       * Limpia todos los datos y filtros
       */
      clearData: () => set({ ...defaultCenterLayerState }),
    }),
    {
      name: '@app/center-layer',
      partialize: (state: CenterLayerStoreRepository) => ({
        specificFilters: state.specificFilters,
        careerFilter: state.careerFilter,
      }),
    },
  ),
);
